import { Button } from '@design-systems/canvas-ui'
import Icon, { ChevronLeftIcon } from '@design-systems/react-icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { useLogClick } from 'utils/analytics'

interface BackButtonProps {
    fallbackPath?: string
    preserveQueryParams?: string[]
}

const buildSearchForFallback = (
    currentSearch: string,
    preserveQueryParams?: string[],
) => {
    if (!preserveQueryParams) {
        return currentSearch
    }

    const existingParams = new URLSearchParams(currentSearch)
    const nextParams = new URLSearchParams()

    preserveQueryParams.forEach((param) => {
        const value = existingParams.get(param)
        if (value) {
            nextParams.set(param, value)
        }
    })

    const serialized = nextParams.toString()
    return serialized ? `?${serialized}` : ''
}

export const BackButton = ({
    fallbackPath,
    preserveQueryParams,
}: BackButtonProps) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t, currentLocation } = useEssentials()
    const { logClick } = useLogClick('back button')

    const handleBackClick = () => {
        const isBrandDetailsRoute = location.pathname.startsWith('/brands/')
        const isTrailerDetailsRoute =
            location.pathname.startsWith('/trailer/detail')

        if (isBrandDetailsRoute) {
            logClick('brands from back button')
            void navigate('/brands')
        } else if (isTrailerDetailsRoute) {
            logClick('trailer details from back button')
            void navigate(`/trailer/summary?locationId=${currentLocation}`)
        } else if (fallbackPath) {
            logClick('configured back button')
            const search = buildSearchForFallback(
                location.search,
                preserveQueryParams,
            )
            void navigate(`${fallbackPath}${search}`)
        } else {
            logClick('general back button')
            void navigate(-1)
        }
    }

    return (
        <Button
            isIconOnly
            aria-label={t(TRANSLATION_KEYS.common.goBackAriaLabel)}
            onClick={handleBackClick}
            variant="ghost"
        >
            <Icon
                svg={ChevronLeftIcon}
                className="navbar-icon"
                size="lg"
                aria-hidden
            />
        </Button>
    )
}
